import { all, call, fork, put, takeLatest, takeLeading } from "redux-saga/effects";
import { getQuery, postQuery, putQuery, deleteQuery } from "utils/api";
import { Toast } from "utils/toast";
import { actions } from "./action";
import { actions as loaderAction } from "redux/Loader/action";

const normalizeStandaloneSubjects = (standaloneQualifications) => {
  if (!Array.isArray(standaloneQualifications)) {
    return [];
  }

  return standaloneQualifications
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const subject = {};
      const title = typeof item.title === "string" ? item.title.trim() : "";
      const predictedGrade =
        typeof item.predictedGrade === "string"
          ? item.predictedGrade.trim()
          : "";
      const actualGrade =
        typeof item.actualGrade === "string" ? item.actualGrade.trim() : "";

      if (title) subject.title = title;
      if (predictedGrade) subject.predictedGrade = predictedGrade;
      if (actualGrade) subject.actualGrade = actualGrade;

      return subject;
    })
    .filter((item) => Object.keys(item).length > 0);
};

const buildBulkExistingUserUpdatePayload = (input) => {
  if (!input || typeof input !== "object") {
    return null;
  }

  const email =
    typeof input.email === "string" ? input.email.trim().toLowerCase() : "";
  if (!email) {
    return null;
  }

  const payload = { email };
  const name = typeof input.name === "string" ? input.name.trim() : "";
  const dateOfBirth =
    typeof input.date_of_birth === "string" ? input.date_of_birth.trim() : "";

  if (name) payload.name = name;
  if (dateOfBirth) payload.date_of_birth = dateOfBirth;
  if (input.status) payload.status = input.status;
  if (input.playerCategory) payload.playerCategory = input.playerCategory;
  if (Array.isArray(input.coursesId) && input.coursesId.length > 0) {
    payload.coursesId = input.coursesId;
  }
  if (
    Array.isArray(input.workExperiencesId) &&
    input.workExperiencesId.length > 0
  ) {
    payload.workExperiencesId = input.workExperiencesId;
  }

  if (
    input.standaloneQualGrades &&
    typeof input.standaloneQualGrades === "object"
  ) {
    const normalizedByCourse = Object.entries(input.standaloneQualGrades).reduce(
      (acc, [courseId, value]) => {
        const subjects = normalizeStandaloneSubjects(value?.subjects);
        if (subjects.length > 0) {
          acc[courseId] = { subjects };
        }
        return acc;
      },
      {}
    );
    if (Object.keys(normalizedByCourse).length > 0) {
      payload.standaloneQualGrades = normalizedByCourse;
    }
  }

  const standaloneSubjects = normalizeStandaloneSubjects(
    input.standaloneQualifications
  );
  if (standaloneSubjects.length > 0 && !payload.standaloneQualGrades) {
    payload.standaloneQualGrades = {
      bulk: { subjects: standaloneSubjects },
    };
  }

  return Object.keys(payload).length > 1 ? payload : null;
};

// Rows per request. Each new learner costs several writes server-side, so large
// files sent in one request ran past the gateway timeout with no result.
const BULK_BATCH_SIZE = 20;
const BULK_REQUEST_TIMEOUT_MS = 180000;
const BULK_UPDATE_CONCURRENCY = 4;

const rowIdentity = (row) =>
  String(row?.email || row?.username || "").trim().toLowerCase();

const setBulkProgress = (progress) =>
  put({ type: actions.BULK_UPLOAD_PROGRESS, payload: progress });

export function* createPlayers() {
  yield takeLeading(
    actions.CREATE_PLAYERS_REQUEST,
    function* ({ data, userType }) {
      const rows = Array.isArray(data) ? data : [data];
      const createdUsers = [];
      const existUsers = [];
      const errors = [];
      const createdCategories = [];
      let stoppedAt = null;

      try {
        yield setBulkProgress({ inProgress: true, done: 0, total: rows.length, stage: "create" });

        for (let i = 0; i < rows.length; i += BULK_BATCH_SIZE) {
          const batch = rows.slice(i, i + BULK_BATCH_SIZE);
          let failure = null;
          try {
            const response = yield call(
              postQuery,
              `registrationViaControlCentral`,
              batch,
              { timeout: BULK_REQUEST_TIMEOUT_MS }
            );
            if (response?.data?.error) {
              failure = response.data.message || "The server rejected this batch";
            } else {
              const result = response?.data?.result || {};
              if (Array.isArray(result.user)) createdUsers.push(...result.user);
              if (Array.isArray(result.existUsers)) existUsers.push(...result.existUsers);
              if (Array.isArray(result.errors)) errors.push(...result.errors);
              if (Array.isArray(result.createdCategories)) {
                createdCategories.push(...result.createdCategories);
              }
            }
          } catch (e) {
            if (e._isAuthError) throw e;
            console.error(e);
            failure =
              e.code === "ECONNABORTED"
                ? "The server took too long to respond"
                : e.response?.data?.message || e.message || "Network error";
          }

          if (failure) {
            // The failed batch may have been partly saved; later rows were never sent.
            batch.forEach((row) =>
              errors.push({
                user: rowIdentity(row),
                notSaved: true,
                error: `${failure}. This row may not have saved — upload the file again (learners already created will show as existing).`,
              })
            );
            rows.slice(i + BULK_BATCH_SIZE).forEach((row) =>
              errors.push({
                user: rowIdentity(row),
                notSaved: true,
                error: "Not sent because the upload stopped. Upload the file again to retry.",
              })
            );
            stoppedAt = i;
            break;
          }

          yield setBulkProgress({
            inProgress: true,
            done: Math.min(i + BULK_BATCH_SIZE, rows.length),
            total: rows.length,
            stage: "create",
          });
        }

        let mergedUsers = [...createdUsers];
        let mergedExistUsers = [...existUsers];
        let mergedErrors = [...errors];

        const inputByEmail = rows.reduce((acc, row) => {
          const key =
            typeof row?.email === "string" ? row.email.trim().toLowerCase() : "";
          if (key) acc[key] = row;
          return acc;
        }, {});

        const updates = [];
        new Set(
          existUsers.map((existing) =>
            typeof existing?.user === "string"
              ? existing.user.trim().toLowerCase()
              : ""
          )
        ).forEach((email) => {
          if (!email || !inputByEmail[email]) return;
          const payload = buildBulkExistingUserUpdatePayload(inputByEmail[email]);
          if (payload) updates.push({ email, payload });
        });

        if (updates.length > 0) {
          const updatedExistingUsers = [];
          const resolvedExistingEmails = new Set();

          yield setBulkProgress({ inProgress: true, done: 0, total: updates.length, stage: "update" });
          for (let i = 0; i < updates.length; i += BULK_UPDATE_CONCURRENCY) {
            const chunk = updates.slice(i, i + BULK_UPDATE_CONCURRENCY);
            const responses = yield all(
              chunk.map(({ payload }) =>
                call(function* () {
                  try {
                    return yield call(putQuery, `updateControlCenterUser`, payload);
                  } catch (e) {
                    if (e._isAuthError) throw e;
                    console.error(e);
                    return null;
                  }
                })
              )
            );
            responses.forEach((updateResponse, j) => {
              if (updateResponse && !updateResponse.data?.error) {
                updatedExistingUsers.push({
                  ...updateResponse.data.result,
                  wasExistingUpdate: true,
                });
                resolvedExistingEmails.add(chunk[j].email);
              }
            });
            yield setBulkProgress({
              inProgress: true,
              done: Math.min(i + BULK_UPDATE_CONCURRENCY, updates.length),
              total: updates.length,
              stage: "update",
            });
          }

          if (updatedExistingUsers.length > 0) {
            mergedUsers = [...mergedUsers, ...updatedExistingUsers];
            mergedExistUsers = existUsers.filter(
              (existing) =>
                !resolvedExistingEmails.has(
                  typeof existing?.user === "string"
                    ? existing.user.trim().toLowerCase()
                    : ""
                )
            );
            mergedErrors = errors.filter(
              (errorItem) =>
                !resolvedExistingEmails.has(
                  typeof errorItem?.user === "string"
                    ? errorItem.user.trim().toLowerCase()
                    : ""
                )
            );
            Toast(
              "success",
              `${updatedExistingUsers.length} existing ${
                userType || "user"
              }(s) updated from bulk upload`
            );
          }
        }

        // Category changes the server applied to existing learners.
        const categoryUpdates = mergedExistUsers.filter(
          (existing) => existing?.categoryUpdated
        );
        if (categoryUpdates.length > 0) {
          Toast(
            "success",
            `${categoryUpdates.length} existing ${
              userType || "user"
            }(s) moved into their category`
          );
        }

        if (createdCategories.length > 0) {
          Toast(
            "success",
            `New categories created: ${createdCategories
              .map((category) => category.name)
              .join(", ")}`
          );
          yield put({
            type: actions.GET_PLAYER_CATEGORY_REQUEST,
            clubId: rows[0]?.club?.id,
          });
        }

        yield put({ type: loaderAction.CLOSE_LOADER });
        yield put({
          type: actions.CREATE_PLAYERS_SUCCESS,
          payload: {
            user: mergedUsers,
            existUsers: mergedExistUsers,
            errors: mergedErrors,
            createdCategories,
          },
        });

        if (stoppedAt !== null) {
          Toast(
            "error",
            `Upload stopped after ${stoppedAt} of ${rows.length} rows. See the Status column, then upload the file again to retry.`
          );
        } else if (
          mergedErrors.length <= 0 &&
          mergedExistUsers.every((existing) => existing?.categoryUpdated)
        ) {
          Toast(
            "success",
            `${userType || "User"} details submitted successfully`
          );
        }
      } catch (e) {
        console.error(e);
        yield put({ type: loaderAction.CLOSE_LOADER });
        if (!e._isAuthError) {
          Toast("error", e.response?.data?.message || "Failed to create user");
        }
      } finally {
        yield setBulkProgress({ inProgress: false, done: 0, total: 0, stage: null });
      }
    }
  );
}

export function* getPlayers() {
  yield takeLatest(actions.GET_PLAYERS_REQUEST, function* ({ data }) {
    try {
      
      const response = yield getQuery(
        `users/controlCenter?clubId=${data?.club?.id}`
      );
      
      if (response.data.result?.length > 0) {
        // Log transferableSkills structure for first player
        const firstPlayer = response.data.result[0];
        console.log("📊 First player transferableSkills structure:", {
          playerId: firstPlayer?.id,
          playerName: firstPlayer?.name,
          hasTransferableSkills: !!firstPlayer?.transferableSkills,
          transferableSkillsType: Array.isArray(firstPlayer?.transferableSkills) ? 'array' : typeof firstPlayer?.transferableSkills,
          transferableSkills: firstPlayer?.transferableSkills,
          isActive: firstPlayer?.transferableSkills?.isActive,
          skillsCount: firstPlayer?.transferableSkills?.transferableSkills?.length || 0
        });
        
        console.log("First 3 players:", response.data.result.slice(0, 3).map(p => ({
          name: p.name,
          id: p.id,
          category: p.playerCategory,
          transferableSkills: p.transferableSkills ? {
            isActive: p.transferableSkills.isActive,
            skillsCount: p.transferableSkills.transferableSkills?.length || 0,
            type: Array.isArray(p.transferableSkills) ? 'array' : typeof p.transferableSkills
          } : null
        })));
      }
      
      if (!response.data.error) {
        yield put({
          type: actions.GET_PLAYERS_SUCCESS,
          payload: response.data.result,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export function* updatePlayer() {
  yield takeLatest(
    actions.UPDATE_PLAYER_REQUEST,
    function* ({ data, userType }) {
      try {
        const response = yield putQuery(`updateControlCenterUser`, data);
        if (!response.data.error) {
          const { staleTransferableSkillIds, ...savedPlayer } =
            response.data.result || {};
          yield put({
            type: actions.UPDATE_PLAYER_SUCCESS,
            payload: savedPlayer,
          });
          if (staleTransferableSkillIds?.length) {
            Toast(
              "warning",
              "Some transferable skill changes were not saved because the learner updated them first. Their latest values are now shown."
            );
          }

          if (data?.isArchived === true) {
            yield put({
              type: actions.REMOVE_PLAYER_SUCCESS,
              payload: response?.data?.result,
            });
          }

          if (data?.isArchived === false) {
            yield put({
              type: actions.UNREMOVE_PLAYER_SUCCESS,
              payload: response?.data?.result,
            });
          }

          if (data?.accountStatus === "alumni") {
            yield put({
              type: actions.MOVE_TO_ALUMNI_SUCCESS,
              payload: [data.id],
            });
          }
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });

          Toast("success", `${userType} Updated Successfully!`);
        }
      } catch (e) {
        console.error(e);
        yield put({
          type: actions.CATCH_ERROR,
          payload: true,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
      }
    }
  );
}

export function* deletePlayer() {
  yield takeLatest(
    actions.DELETE_PLAYER_REQUEST,
    function* ({ data, userType, navigate }) {
      try {
        const response = yield deleteQuery(`player/remove?playerId=${data}`);

        if (!response.data.error) {
          yield put({
            type: actions.DELETE_PLAYER_SUCCESS,
            payload: data,
          });
          Toast("success", `${userType} Removed Successfully!`);
        }
      } catch (e) {
        console.error(e);
        yield put({
          type: actions.CATCH_ERROR,
          payload: true,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
      }
    }
  );
}

export function* getClub() {
  yield takeLatest(actions.GET_CLUB_REQUEST, function* () {
    try {
      const response = yield getQuery(`club`);

      if (!response.data.error) {
        yield put({
          type: actions.GET_CLUB_SUCCESS,
          payload: response.data.result.data,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export function* getAllPlayers() {
  yield takeLatest(actions.GET_ALL_PLAYERS_REQUEST, function* ({ data }) {
    try {
      
      let query = `players?keyword=${data.keyword}&limit=18&skip=${data.skip}`;
      if (data.clubId) {
        query = `players?clubId=${data.clubId}&keyword=${data.keyword}&limit=18&skip=${data.skip}`;
      }
      if (data.category) query += `&category=${encodeURIComponent(data.category)}`;
      
      // A–Z, and the all-users list leaves out archived organisations.
      const response = yield postQuery(query, {
        ...data.body,
        hideArchivedOrgs: true,
        sort: "name",
      });
      
      
      if (response.data.result.user.length > 0) {
        yield put({
          type: actions.GET_ALL_PLAYERS_SUCCESS,
          payload: response.data.result.user,
        });
        yield put({
          type: actions.CATCH_ERROR,
          payload: false,
        });
      } else {
        yield put({
          type: actions.CATCH_ERROR,
          payload: true,
        });
      }
    } catch (e) {
      yield put({
        type: actions.CATCH_ERROR,
        payload: true,
      });
      // yield put({
      // });
      console.error(e);
    }
  });
}

export function* getArchivedPlayers() {
  yield takeLatest(actions.GET_ARCHIVED_PLAYERS_REQUEST, function* ({ data }) {
    try {
      
      let query = `players?keyword=${data.keyword}&limit=18&skip=${data.skip}`;
      if (data.body?.clubId && data.body.clubId.length > 0) {
        // Use first clubId in array for query string
        query = `players?clubId=${data.body.clubId[0]}&keyword=${data.keyword}&limit=18&skip=${data.skip}`;
      }
      
      
      const response = yield postQuery(query, data.body);
      yield put({
        type: actions.GET_ARCHIVED_PLAYERS_SUCCESS,
        payload: response.data.result.user,
      });
    } catch (e) {
      // yield put({
      // });
      // yield put({
      // });
      console.error(e);
    }
  });
}

export function* getPlayerStatus() {
  yield takeLatest(actions.GET_PLAYER_STATUS_REQUEST, function* () {
    try {
      const response = yield getQuery(`player/status`);

      if (!response.data.error) {
        yield put({
          type: actions.GET_PLAYER_STATUS_SUCCESS,
          payload: response.data.result,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export function* getPlayerCategory() {
  yield takeLatest(actions.GET_PLAYER_CATEGORY_REQUEST, function* ({ clubId }) {
    try {
      const url = clubId ? `player/category?clubId=${clubId}` : `player/category`;
      const response = yield getQuery(url);

      if (!response.data.error) {
        yield put({
          type: actions.GET_PLAYER_CATEGORY_SUCCESS,
          payload: response.data.result,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export function* getCoursesOfUser() {
  yield takeLatest(actions.GET_PLAYER_COURSES_REQUEST, function* ({ data }) {
    try {
      const response = yield getQuery(`user-courses?userId=${data.userId}`);

      if (!response.data.error) {
        yield put({
          type: actions.GET_PLAYER_COURSES_SUCCESS,
          payload: { userId: data.userId, courses: response.data.result },
        });
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
      }
    } catch (e) {
      console.error(e);
      yield put({
        type: loaderAction.CLOSE_LOADER,
      });
    }
  });
}

export function* createTransferableSkill() {
  yield takeLatest(
    actions.ADD_TRANSFERABLE_REQUEST,
    function* ({ data, navigate }) {
      try {
        const response = yield putQuery(`transferable-skill/add`, data);
        if (!response.data.error) {
          yield put({
            type: actions.ADD_TRANSFERABLE_SUCCESS,
            payload: response.data.result,
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
          Toast("success", "Successfully Created!");
        }
      } catch (e) {
        console.error(e);
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message);
      }
    }
  );
}

export function* updateTransferableSkill() {
  yield takeLatest(
    actions.UPDATE_TRANSFERABLE_REQUEST,
    function* ({ data }) {
      try {
        const response = yield putQuery(`transferable-skill`, data);
        console.error("📥 API Response - updateTransferableSkill:", {
          error: response.data.error,
          result: response.data.result,
          isActive: response.data.result?.isActive,
          skillsCount: response.data.result?.transferableSkills?.length || 0
        });
        
        if (!response.data.error) {
          // Include userId in payload so reducer can update the right player
          const payload = {
            userId: data.userId,
            isActive: data.isActive,
            resource: response.data.result, // Full resource from backend (includes all skills)
          };
          yield put({
            type: actions.UPDATE_TRANSFERABLE_SUCCESS,
            payload: payload,
          });
          yield put({
            type: loaderAction.CLOSE_LOADER,
          });
          Toast("success", "Successfully Updated!");
        } else {
          console.error("❌ API returned error:", response.data.error);
        }
      } catch (e) {
        console.error("❌ API call failed:", e);
        console.error("Error details:", e.response?.data);
        yield put({
          type: loaderAction.CLOSE_LOADER,
        });
        if (!e._isAuthError) Toast("error", e.response?.data?.message || "Failed to update transferable skills");
      }
    }
  );
}

export function* deleteTransferableSkill() {
  yield takeLatest(
    actions.DELETE_TRANSFERABLE_REQUEST,
    function* ({ data }) {
      try {
        const response = yield deleteQuery(`transferable-skill`, data);
        if (!response.data.error) {
          yield put({
            type: actions.DELETE_TRANSFERABLE_SUCCESS,
            payload: { userId: data.userId, resource: response.data.result },
          });
          Toast("success", "Successfully Deleted!");
        }
      } catch (e) {
        console.error(e);
        if (!e._isAuthError)
          Toast(
            "error",
            e.response?.data?.message || "Failed to delete transferable skill"
          );
      }
    }
  );
}

export function* getTransferableSkill() {
  yield takeLatest(actions.GET_TRANSFERABLE_REQUEST, function* ({ data }) {
    try {
      const response = yield getQuery(
        `transferable-skill?userId=${encodeURIComponent(data.userId)}`
      );
      if (!response.data.error) {
        yield put({
          type: actions.GET_TRANSFERABLE_SUCCESS,
          payload: { userId: data.userId, resource: response.data.result },
        });
      }
    } catch (e) {
      // No skills document yet, or the backend predates this endpoint: the
      // copy from the players list stays.
      console.error(e);
    }
  });
}

export function* getAlumniPlayers() {
  yield takeLatest(actions.GET_ALUMNI_PLAYERS_REQUEST, function* ({ data }) {
    try {
      const url = data.clubId ? `users/alumni?clubId=${data.clubId}` : `users/alumni`;
      const response = yield getQuery(url);
      if (!response.data.error) {
        yield put({
          type: actions.GET_ALUMNI_PLAYERS_SUCCESS,
          payload: response.data.result,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export function* bulkMoveToAlumni() {
  yield takeLatest(
    actions.BULK_MOVE_TO_ALUMNI_REQUEST,
    function* ({ emails: ids, userType }) {
      try {
        for (const id of ids) {
          yield call(putQuery, `updateControlCenterUser`, {
            id,
            accountStatus: "alumni",
          });
        }
        yield put({
          type: actions.MOVE_TO_ALUMNI_SUCCESS,
          payload: ids,
        });
        Toast("success", `${ids.length} ${userType}(s) moved to alumni`);
      } catch (e) {
        console.error(e);
        Toast("error", "Failed to move users to alumni");
      }
    }
  );
}

export function* bulkArchive() {
  yield takeLatest(
    actions.BULK_ARCHIVE_REQUEST,
    function* ({ emails: ids, userType }) {
      try {
        const results = [];
        for (const id of ids) {
          const response = yield call(putQuery, `updateControlCenterUser`, {
            id,
            isArchived: true,
          });
          if (response?.data?.result) results.push(response.data.result);
        }
        for (const result of results) {
          yield put({ type: actions.REMOVE_PLAYER_SUCCESS, payload: result });
        }
        Toast("success", `${ids.length} ${userType}(s) archived`);
      } catch (e) {
        console.error(e);
        Toast("error", "Failed to archive users");
      }
    }
  );
}

export function* getIndividualUsers() {
  yield takeLatest(actions.GET_INDIVIDUAL_USERS_REQUEST, function* () {
    try {
      const response = yield getQuery(`users/individual`);
      if (!response.data.error) {
        yield put({
          type: actions.GET_INDIVIDUAL_USERS_SUCCESS,
          payload: response.data.result,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });
}

export function* archiveIndividualUser() {
  yield takeLatest(
    actions.ARCHIVE_INDIVIDUAL_USER_REQUEST,
    function* ({ id }) {
      try {
        const response = yield call(putQuery, `updateControlCenterUser`, {
          id,
          isArchived: true,
        });
        if (response?.data?.error) {
          Toast("error", "Failed to archive account");
          return;
        }
        Toast("success", "Account archived");
        // The list query excludes archived users, so refetch rather than
        // filtering locally — that keeps this page and the server in step.
        yield put({ type: actions.GET_INDIVIDUAL_USERS_REQUEST });
      } catch (e) {
        console.error(e);
        Toast("error", "Failed to archive account");
      }
    }
  );
}

export function* restoreAlumni() {
  yield takeLatest(
    actions.RESTORE_ALUMNI_REQUEST,
    function* ({ id, assignClub, userType }) {
      try {
        if (!assignClub || !assignClub.id) {
          Toast("error", "Please select an organisation before restoring this player");
          return;
        }
        yield put({ type: loaderAction.START_LOADER });
        const response = yield call(putQuery, `updateControlCenterUser`, {
          id,
          accountStatus: "active",
          assignClub,
        });
        yield put({ type: loaderAction.CLOSE_LOADER });
        if (!response.data.error) {
          yield put({
            type: actions.RESTORE_ALUMNI_SUCCESS,
            payload: { id, player: response.data.result },
          });
          Toast("success", `${userType || "Player"} moved back to organisation`);
        } else {
          Toast(
            "error",
            response?.data?.message ||
              response?.data?.error ||
              "Failed to move player to organisation"
          );
        }
      } catch (e) {
        yield put({ type: loaderAction.CLOSE_LOADER });
        const details =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.response?.data?.body?.message ||
          e?.response?.data?.body?.error ||
          e?.message;
        Toast(
          "error",
          details || "Failed to move player to organisation"
        );
      }
    }
  );
}

export function* createIndividualUser() {
  yield takeLatest(
    actions.CREATE_INDIVIDUAL_USER_REQUEST,
    function* ({ data }) {
      try {
        const response = yield postQuery(`users/individual`, data);
        if (!response.data.error) {
          yield put({
            type: actions.CREATE_INDIVIDUAL_USER_SUCCESS,
            payload: response.data.result,
          });
          Toast("success", "Individual account created — login details sent by email");
        }
      } catch (e) {
        console.error(e);
        Toast("error", e?.response?.data?.message || "Failed to create account");
      }
    }
  );
}

export default function* playerSaga() {
  yield all([
    fork(getPlayers),
    fork(createPlayers),
    fork(updatePlayer),
    fork(getClub),
    fork(getAllPlayers),
    fork(getPlayerStatus),
    fork(getPlayerCategory),
    fork(getArchivedPlayers),
    fork(deletePlayer),
    fork(getCoursesOfUser),
    fork(createTransferableSkill),
    fork(updateTransferableSkill),
    fork(deleteTransferableSkill),
    fork(getTransferableSkill),
    fork(getAlumniPlayers),
    fork(bulkMoveToAlumni),
    fork(bulkArchive),
    fork(createIndividualUser),
    fork(getIndividualUsers),
    fork(archiveIndividualUser),
    fork(restoreAlumni),
  ]);
}
